import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';
import { FeedbackErrorBoundary } from '~/pages/Feedback/FeedbackErrorBoundary';
import { AuthReceiver } from '~/AuthReceiver';
import { Flags } from '~/pages/Flags/Flags';
import { Auth0Error } from '~/pages/Auth0Error/Auth0Error';
import { VerifyUser } from '~/pages/VerifyUser/VerifyUser';
import { Auth0Layout } from '~/components/Layout/Auth0Layout';
import { ProtectedUser } from '~/components/Protected/ProtectedUser';
import { UserAccountProvider } from '~/contexts/UserAccount/UserAccountProvider';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { PageNotFound } from '~/pages/PageNotFound';
import { AppWideErrorLayout } from '~/components/Layout/AppWideErrorLayout';
import GroupOrders from './pages/GroupOrders/GroupOrders';
import OrderHistory from './pages/OrderHistory/OrderHistory';
import { Account } from './pages/Settings/Account';
import OrderHistorySingle from './pages/OrderHistory/OrderHistorySingle';
import { FullLayout } from './components/Layout/FullLayout';
import { EmptyLayout } from './components/Layout/EmptyLayout';
import { ProtectedCodeOrUser } from './components/Protected/ProtectedCodeOrUser';
import { AppProviders } from './AppProviders';
import {
    AccountPathsGroup,
    ArtsPath,
    BaseContext,
    DesignsPath,
    FeedbackPath,
    FlagsPath,
    GroupOrdersPath,
    OrdersPath,
    SharedDesignsPath,
    SingleOrderPath,
    StoresPath,
} from './config';
import { DesignsAndArts } from './pages/DesignsAndArts';
import { SharedDesigns } from './pages/SharedDesigns/SharedDesigns';
import { Stores } from './pages/Stores/Stores';
import { Feedback } from './pages/Feedback/Feedback';
import { DesignDetail } from './pages/SavedDesigns/DesignDetail/DesignDetail';

const ProtectedPaths = [
    ...AccountPathsGroup,
    ArtsPath,
    DesignsPath,
    FlagsPath,
    GroupOrdersPath,
    OrdersPath,
    SingleOrderPath,
    SharedDesignsPath,
    StoresPath,
];

export default function App() {
    return (
        <ErrorBoundary withLayout errorMessage="An error ocurred while loading the accounts portal">
            <Router basename={BaseContext}>
                <AppProviders>
                    <ErrorBoundary withLayout errorMessage="An error ocurred while loading this page">
                        <Switch>
                            <Route exact path="/">
                                <Redirect to={DesignsPath} />
                            </Route>
                            <Route path={`${DesignsPath}/:compositeId`}>
                                <UserAccountProvider>
                                    <EmptyLayout>
                                        <DesignDetail />
                                    </EmptyLayout>
                                </UserAccountProvider>
                            </Route>
                            <Route path={ProtectedPaths}>
                                <ProtectedUser>
                                    <UserAccountProvider>
                                        <Route path={SingleOrderPath}>
                                            <EmptyLayout>
                                                <OrderHistorySingle />
                                            </EmptyLayout>
                                        </Route>
                                        <Route path={[DesignsPath, ArtsPath]}>
                                            <FullLayout>
                                                <DesignsAndArts />
                                            </FullLayout>
                                        </Route>
                                        <Route path={SharedDesignsPath}>
                                            <FullLayout>
                                                <SharedDesigns />
                                            </FullLayout>
                                        </Route>
                                        <Route path={GroupOrdersPath}>
                                            <FullLayout>
                                                <GroupOrders />
                                            </FullLayout>
                                        </Route>
                                        <Route path={OrdersPath}>
                                            <FullLayout>
                                                <OrderHistory />
                                            </FullLayout>
                                        </Route>
                                        <Route path={StoresPath}>
                                            <FullLayout>
                                                <Stores />
                                            </FullLayout>
                                        </Route>
                                        <Route path={AccountPathsGroup}>
                                            <FullLayout>
                                                <Account />
                                            </FullLayout>
                                        </Route>
                                        <Route path={FlagsPath}>
                                            <FullLayout>
                                                <Flags />
                                            </FullLayout>
                                        </Route>
                                    </UserAccountProvider>
                                </ProtectedUser>
                            </Route>
                            <Route path="/auth/verify">
                                <Auth0Layout>
                                    <VerifyUser />
                                </Auth0Layout>
                            </Route>
                            <Route path="/auth/error">
                                <Auth0Layout>
                                    <Auth0Error />
                                </Auth0Layout>
                            </Route>
                            <Route path="/auth">
                                <AuthReceiver />
                            </Route>
                            <Route path={FeedbackPath}>
                                <ProtectedCodeOrUser>
                                    <UserAccountProvider>
                                        <EmptyLayout>
                                            <FeedbackErrorBoundary>
                                                <Feedback />
                                            </FeedbackErrorBoundary>
                                        </EmptyLayout>
                                    </UserAccountProvider>
                                </ProtectedCodeOrUser>
                            </Route>
                            <Route path="/*">
                                <AppWideErrorLayout>
                                    <PageNotFound />
                                </AppWideErrorLayout>
                            </Route>
                        </Switch>
                    </ErrorBoundary>
                </AppProviders>
            </Router>
        </ErrorBoundary>
    );
}
